import React, { useCallback } from 'react'

import { arrayOf, number, shape, string } from 'prop-types'

import usePageView from 'Pods/Shared/Hooks/usePageView'
import I18n from 'Pods/Shared/I18n'
import SolidgatePaymentSection from 'Pods/Shared/SolidgatePaymentSection/SolidgatePaymentSection'

import ExtraUsageTerms from './Components/ExtraUsageTerms/ExtraUsageTerms'
import OrderSummary from './Components/OrderSummary/OrderSummary'
import useExtraUsageCheckout from './Hooks/useExtraUsageCheckout'
import useExtraUsageCheckoutTracking from './Hooks/useExtraUsageCheckoutTracking'

const TRANSLATION_KEY_PREFIX = 'extra_usage_checkout'
const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'

function ExtraUsageCheckout({
  merchant,
  signature,
  paymentIntent,
  orderSummary,
  returnPath = DEFAULT_RETURN_PATH,
  locale = 'en',
}) {
  const {
    isFormValid,
    isPaymentProcessing,
    handleFormReady,
    handleFormValidityChange,
    handlePaymentProcessing,
    submitPaymentForm,
  } = useExtraUsageCheckout()

  const tracking = useExtraUsageCheckoutTracking()

  usePageView(tracking.trackView)

  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  return (
    <div className="extra-usage-checkout">
      <div className="extra-usage-checkout__main">
        <div className="extra-usage-checkout__content">
          {!isPaymentProcessing && (
            <>
              <h1 className="extra-usage-checkout__title cgt-heading-300">
                {t(`${TRANSLATION_KEY_PREFIX}.title`)}
              </h1>
              <p className="extra-usage-checkout__subtitle cgt-body-md">
                {t(`${TRANSLATION_KEY_PREFIX}.subtitle`)}
              </p>
            </>
          )}

          <SolidgatePaymentSection
            merchant={merchant}
            signature={signature}
            paymentIntent={paymentIntent}
            returnPath={returnPath}
            locale={locale}
            onFormReady={handleFormReady}
            onFormValidityChange={handleFormValidityChange}
            onPaymentProcessing={handlePaymentProcessing}
            translationKeyPrefix={TRANSLATION_KEY_PREFIX}
            tracking={tracking}
          />
        </div>
      </div>

      <div className="extra-usage-checkout__order-details">
        <OrderSummary
          locale={locale}
          itemTitle={orderSummary.itemTitle}
          itemBullets={orderSummary.itemBullets}
          itemPrice={orderSummary.itemPrice}
          subtotal={orderSummary.subtotal}
          vatAmount={orderSummary.vatAmount}
          total={orderSummary.total}
          onPay={submitPaymentForm}
          disabled={!isFormValid || isPaymentProcessing}
        />
        <ExtraUsageTerms locale={locale} />
      </div>
    </div>
  )
}

ExtraUsageCheckout.propTypes = {
  merchant: string.isRequired,
  signature: string.isRequired,
  paymentIntent: string.isRequired,
  orderSummary: shape({
    itemTitle: string.isRequired,
    itemBullets: arrayOf(string).isRequired,
    itemPrice: number.isRequired,
    subtotal: number.isRequired,
    vatAmount: number,
    total: number.isRequired,
  }).isRequired,
  returnPath: string,
  locale: string,
}

export default ExtraUsageCheckout
